<template>
  <v-form v-model="valid">
    <v-row class="ma-0">
      <v-col class="overview-world-img py-11">
        <img v-if="!loading" :src="require('@/assets/images/overview-world.svg')" alt="overview-world">
        <v-progress-circular
          v-else
          indeterminate
          color="amber"
        />
      </v-col>
      <v-col class="col-6 pa-0 overview-container">
        <v-row class="overview-card mt-5 overview-map-list">
          <v-col class="pa-0">
            <v-row class="ma-0">
              <div class="overview-card-title mb-3">{{ $t('map.tolldomains.overview.map_list') }}</div>
            </v-row>
            <v-row class="overview-action-container">
              <v-col>
                <v-row
                  class="overview-add-map-container pa-7 mb-0"
                  :class="!model.useVariant ? 'selected' : ''"
                  dense
                  align="center"
                  justify="center"
                  @drop.prevent="onDrop($event)"
                  @dragover.prevent="dragover = true"
                  @dragenter.prevent="dragover = true"
                  @dragleave.prevent="dragover = false"
                >
                  <p class="block-title">
                    {{ $t('map.tolldomains.geometry.drop') }}
                  </p>
                  <v-icon class="upload-file" size="45" rounded>
                    mdi-file-upload-outline
                  </v-icon>
                  <div class="input-actions">
                    <v-file-input
                      v-model="model.geoZip"
                      class="file-input"
                      :color="colors.secondary"
                      hide-details
                      outlined
                      rounded
                      dense
                      :placeholder="$t('map.tolldomains.geometry.choose')"
                      prepend-icon=""
                      @change="model.useVariant = false"
                    >
                      <template v-slot:selection="{}">
                        {{ $t('map.tolldomains.geometry.choose') }}
                      </template>
                    </v-file-input>
                  </div>
                </v-row>
                <v-row class="middle-label"><span>{{ $t('map.tolldomains.variant.upload_or_variant') }}</span></v-row>
                <v-row
                  class="overview-add-map-container variant-block pa-7 mb-0"
                  :class="model.useVariant ? 'selected' : ''"
                  dense
                  align="center"
                  justify="center"
                >
                  <p class="block-title variant-block">
                    {{ $t('map.tolldomains.geometry.reuse_variant') }}
                  </p>
                  <div class="input-actions variant-block">
                    <v-select
                      id="variant-list"
                      :items="getVariants"
                      :item-text="variant => variant.name"
                      :item-value="variant => variant"
                      dark
                      outlined
                      dense
                      class="variant-select"
                      :rules="[!variant404 || $t('common.error.forms.inputs.validator.wrong_variant')]"
                      @change="setVariant"
                    />
                  </div>
                </v-row>
              </v-col>
              <v-col>
                <v-row v-if="model.useVariant" class="ma-0 overview-file-name">
                  <div class="mt-5 mb-1">{{ $t('map.tolldomains.overview.variant_name') }}</div>
                  <v-text-field
                    class="file-name-input"
                    outlined
                    dark
                    dense
                    :value="model.variantName ? model.variantName : ''"
                    :rules="required"
                    disabled
                    hide-details
                  />
                </v-row>
                <v-row v-else class="ma-0 overview-file-name">
                  <div class="mt-5 mb-1">{{ $t('map.tolldomains.overview.map_filename') }}</div>
                  <v-text-field
                    class="file-name-input"
                    outlined
                    dark
                    dense
                    :value="model.geoZip ? model.geoZip.name : ''"
                    :rules="required"
                    disabled
                    hide-details
                  />
                </v-row>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-form>
</template>

<script lang="ts">
import { Component, Mixins, Prop, Watch } from 'vue-property-decorator';
import { DatePicker } from 'ant-design-vue';
import { MasterAccountInfo } from 'ctp-user-auth-service';
import { Variant } from 'ctp-toll-domain';
import moment from 'moment';
import { colors } from '@/constants/theme/colors';
import './styles/overview-general.scss';
import { store as userStore } from '@/store/modules/user/types';
import { store as tollStore } from '@/store/modules/tollDomain/types';
import Validation from '@/mixins/Validation';

@Component({
  components: {
    DatePicker
  }
})
export default class OverviewGeneral extends Mixins(Validation) {
  @Prop()
  private index!: number;

  @Prop()
  private loading!: boolean;

  @userStore.Getter
  private getSelectedMasterAccount: MasterAccountInfo;

  @tollStore.Getter
  private getVariants: Variant[];

  @tollStore.Action
  private fetchCurrentGeometry: (variantId: number) => Promise<void>;

  // Whether we choose to modify a variant or upload a new file
  private useVariant = false;

  private valid = false;

  private variant404 = false;

  private colors = colors;

  private format = 'DD/MM/YYYY';

  private model: any = {};

  private required = [this.requiredRule('common.error.forms.inputs.validator.empty')];

  private disabledDate(current: moment.Moment) {
    return current.isBefore(moment().startOf('day').add(1, 'day'));
  }

  @Watch('model', { deep: true })
  private updateModel() {
    this.$emit('updateModel', this.index, this.model);
    this.preventContinue(); // Prevent continue if we set a value in the Ant design component (DatePicker)
  }

  @Watch('valid')
  private preventContinue() {
    this.$emit('updateError', this.index, !this.valid);
  }

  @Watch('getSelectedMasterAccount', { immediate: true, deep: true })
  private onMasterChange() {
    const account = this.getSelectedMasterAccount;
    if (account.name) {
      this.$set(this.model, 'masterId', account.id);
    }
  }

  private onDrop(e: any) {
    if (e.dataTransfer.files.length === 1) {
      this.$set(this.model, 'useVariant', false);
      this.$set(this.model, 'geoZip', e.dataTransfer.files[0]);
    }
  }

  private async setVariant(variant: Variant) {
    this.$set(this.model, 'useVariant', true);
    // GET CURRENT GEO
    const response: any = await this.fetchCurrentGeometry(variant.id);
    if (response.message && response.message.status === 404) {
      this.valid = false;
      this.variant404 = true;
    } else {
      this.valid = true;
      this.variant404 = false;
      this.model.currentGeometry = response;
      this.$set(this.model, 'currentGeometry', this.model.currentGeometry);
      this.$set(this.model, 'variantId', variant.id);
      this.$set(this.model, 'variantName', variant.name);
      // Step 2, click on Continue in the OverviewNewperiodStepper component
    }
  }
}
</script>
